import { Modal, message } from "antd";
import { useTranslation } from "react-i18next";
import { EPButton } from "@/components";
import { PublishModalStyles } from "./styled";
import { EPInput } from "@/comp";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useStores } from "@/hooks/use-stores";
import { useState } from "react";
import { observer } from "mobx-react";
import { PublishReportPayloadDto } from "./types";

interface ReportRecord {
  id?: string;
  [key: string]: unknown;
}

const PublishModal = observer(({ open, onClose, selectedReport }: { open: boolean, onClose: () => void, selectedReport?: ReportRecord }) => {
  const { t } = useTranslation();
  const { reportStore } = useStores();
  const [estimatedValue, setEstimatedValue] = useState<string>('');
  const [isError, setIsError] = useState(false);

  const handlePublish = async () => {
    const value = parseFloat(estimatedValue);
    if (!estimatedValue || isNaN(value) || value <= 0) {
      setIsError(true);
      return;
    }

    try {
      if (selectedReport?.id) {
        const payload: PublishReportPayloadDto = {
          estimatedValue: value,
        };
        await reportStore.publishReport(selectedReport.id, payload);
        message.success(t('reportPublishedInProgress'));
        setEstimatedValue('');
        setIsError(false);
        onClose();
        // Refresh reports list
        await reportStore.getReports(reportStore.pagination.page, reportStore.pagination.pageSize);
      }
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as { response?: { status?: number; data?: { message?: string } } };
        if (apiError.response?.status === 400) {
          message.error(apiError.response?.data?.message || t('failedToPublishReport'));
        } else {
          message.error(t('failedToPublishReport'));
        }
      } else {
        message.error(t('failedToPublishReport'));
      }
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={480}
      title={t('addBusinessImpactBeforePublishing')}  
    >
      <PublishModalStyles>
        <div className="publish-modal-description">
            <span className="publish-modal-description-text">
                {t('pleaseEstimateItsPotentialBusinessImpact')}
            </span>
        </div>

        <div className="publish-modal-input">
            <div className="publish-modal-input-label">
                <span className="publish-modal-input-label-text">{t('estimatedValue')}</span>  
            </div>
            <div className={`publish-modal-input-wrapper ${isError ? 'publish-modal-input-wrapper-error' : ''}`}>
                <div className="publish-modal-input-prefix">
                    <span className="publish-modal-input-dollar">$</span>
                    <div className="publish-modal-input-divider"></div>
                </div>
                <EPInput 
                  className="publish-modal-input-field" 
                  value={estimatedValue}
                  onChange={(e) => {
                    setEstimatedValue(e.target.value);
                    setIsError(false);
                  }}
                  type="number"
                />
            </div>
            {isError && <div className="publish-modal-input-error">{t('valueIsRequiredAndMustBeHigherThan0ToPublish')}</div>}
        </div>

        <div className="report-description-container">
                <InfoCircleOutlined className="note-icon" />
                <span className="report-description-text"> {t('noteOncePublishedThisReportWillBeAutomaticallySharedWithAllRecipients')}</span>
            </div>

        <div className="publish-modal-button">
            <EPButton onClick={onClose}>
                {t('Cancel')}
            </EPButton>
            <EPButton 
              type="primary" 
              onClick={handlePublish}
            >
                {t('publishReport')}
            </EPButton>
        </div>
      </PublishModalStyles>
    </Modal>
  );
});

export default PublishModal;